import React, { Component } from 'react';
import { withRouter } from "react-router-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { HTTPService } from "../../../services/httpService";
import ReactGA from 'react-ga4';

const API = new HTTPService();

class CampaignWizard extends Component {
  constructor(props) {
    super(props);

    const campaign = props.campaign || {};
    const isEditing = props.mode === "edit" && campaign.id;

    console.log("CampaignWizard - mode:", props.mode);
    console.log("CampaignWizard - campaign:", campaign);
    console.log("CampaignWizard - raw rules_json:", campaign.rules_json);

    // Parse rules_json
    let parsedRules = {};
    if (campaign.rules_json) {
      if (typeof campaign.rules_json === 'string') {
        try {
          parsedRules = JSON.parse(campaign.rules_json);
          console.log("Parsed rules from string:", parsedRules);
        } catch (e) {
          console.error("Error parsing rules_json:", e);
          parsedRules = {};
        }
      } else if (typeof campaign.rules_json === 'object' && campaign.rules_json !== null) {
        parsedRules = { ...campaign.rules_json };
        console.log("Parsed rules from object:", parsedRules);
      }
    }

    console.log("Final parsedRules:", parsedRules);

    // Helper function to determine initial CTA type (defined here so it's available before state is set)
    const getInitialCTAType = (ctaUrl, campaignType) => {
      if (!ctaUrl) {
        // Default for promotional campaigns is email_specialist
        return campaignType === 'promotional' ? 'email_specialist' : 'estimate_page';
      }
      if (ctaUrl.startsWith('mailto:')) {
        return 'email_specialist';
      }
      if (ctaUrl.includes('{{base_url}}/estimate') || ctaUrl.includes('/estimate?')) {
        return 'estimate_page';
      }
      if (ctaUrl === '{{specialist_website}}' || (ctaUrl.startsWith('http') && !ctaUrl.includes('/estimate'))) {
        return 'specialist_website';
      }
      return 'estimate_page';
    };

    // REVERSE MAPPING: Convert backend field names to UI field names
    // Backend stores: trigger_state ("completed" or "created") and days_since_trigger_min/max
    // UI uses: audience_type ("customers" or "prospects") and days_since_created_min/max

    // Ensure arrays exist
    if (!Array.isArray(parsedRules.service_catalog)) {
      parsedRules.service_catalog = [];
    }

    // Determine audience type from trigger_state field
    let audience_type = "customers"; // Default

    if (parsedRules.trigger_state === 'completed') {
      audience_type = "customers";
    } else if (parsedRules.trigger_state === 'created') {
      audience_type = "prospects";
    } else if (parsedRules.trigger_state && campaign.campaign_type === 'system') {
      // System campaigns have their own trigger_state values (scheduled, etc.)
      audience_type = "request";
    }

    // Map backend days_since_trigger_* to UI days_since_created_* for display
    if (parsedRules.days_since_trigger_min !== undefined) {
      parsedRules.days_since_created_min = parsedRules.days_since_trigger_min;
    }
    if (parsedRules.days_since_trigger_max !== undefined) {
      parsedRules.days_since_created_max = parsedRules.days_since_trigger_max;
    }

    console.log("Determined audience_type from trigger_state:", audience_type);

    // Determine default campaign_type based on agentType prop
    let defaultCampaignType = campaign.campaign_type || "always_on";
    if (!isEditing && props.agentType) {
      if (props.agentType === 'iris') {
        defaultCampaignType = 'system';
      } else if (props.agentType === 'luke') {
        defaultCampaignType = 'always_on';
      }
    }

    this.state = {
      currentStep: 1,
      loading: false,

      // Step 1: Campaign Info + Audience Type (for UI only)
      campaign_name: campaign.campaign_name || "",
      campaign_type: defaultCampaignType,
      audience_type: audience_type, // Derived from customer_type, not stored separately
      // Format dates to YYYY-MM-DD for HTML date input (handles both ISO timestamps and date strings)
      promotional_start_date: campaign.promotional_start_date
        ? campaign.promotional_start_date.substring(0, 10)
        : "",
      promotional_end_date: campaign.promotional_end_date
        ? campaign.promotional_end_date.substring(0, 10)
        : "",
      
      // Step 2: Audience Rules (UI structure - will be mapped to backend format on save)
      rules_json: Object.keys(parsedRules).length > 0 ? parsedRules : {
        days_since_created_min: null, // UI field name (maps to days_since_trigger_min on backend)
        days_since_created_max: null, // UI field name (maps to days_since_trigger_max on backend)
        service_catalog: [],
        // System campaign fields
        trigger_state: null,
        trigger_offset_days: null,
      },
      
      // Step 3: Message
      message_subject: campaign.message_subject || "",
      message_body: campaign.message_body || "", // Plain text for backwards compatibility
      message_body_html: (campaign.message_body_html && campaign.message_body_html.trim() !== "")
                         ? campaign.message_body_html
                         : (campaign.message_body || ""),
      cta_label: campaign.cta_label || (defaultCampaignType === 'promotional' ? 'Email Us' : 'Get a Quote'),
      cta_url: campaign.cta_url || "",
      cta_type: getInitialCTAType(campaign.cta_url, defaultCampaignType),
      delivery_time: campaign.delivery_time || "09:00", // Default to 9 AM
      
      // Audience preview
      audienceCount: 0,
      audienceLoading: false,

      // Campaign Offer fields (for promotional/always_on campaigns)
      campaign_description: campaign.campaign_description || "",
      promo_code: campaign.promo_code || "",
      discount_description: campaign.discount_description || "",

      // Validation
      errors: {}
    };
    
    console.log("CampaignWizard - Final state:", this.state);
    
    this._next = this._next.bind(this);
    this._prev = this._prev.bind(this);

    // Ref for Quill editor (for variable insertion)
    this.quillRef = null;

    // Ref for campaign name input (for auto-focus)
    this.campaignNameRef = null;
  }

  async componentDidMount() {
    console.log("CampaignWizard mounted");
    console.log("Props:", this.props);
    console.log("State after mount:", this.state);

    // Auto-focus campaign name input
    if (this.campaignNameRef) {
      this.campaignNameRef.focus();
    }

    // If editing a promotional/always_on campaign, calculate audience size
    if (this.props.mode === "edit" &&
        this.props.campaign &&
        this.props.campaign.id &&
        this.state.campaign_type !== 'system') {
      console.log("Edit mode - calculating audience size");
      try {
        await this.calculateAudienceSize();
      } catch (error) {
        console.error("Error calculating audience size:", error);
        this.setState({ audienceCount: 0 });
      }
    }
  }

  // Navigation
  _next() {
    let currentStep = this.state.currentStep;
    
    // Validate current step before proceeding
    if (!this.validateCurrentStep()) {
      return;
    }
    
    currentStep = currentStep >= 2 ? 3 : currentStep + 1;
    this.setState({ currentStep });
    
    // Calculate audience when moving to step 3 (only for promotional/always_on campaigns)
    if (currentStep === 3 && this.state.campaign_type !== 'system') {
      this.calculateAudienceSize();
    }
  }

  _prev() {
    let currentStep = this.state.currentStep;
    currentStep = currentStep <= 1 ? 1 : currentStep - 1;
    this.setState({ currentStep });
  }

  // Validation
  validateCurrentStep = () => {
    const { currentStep, campaign_name, campaign_type, audience_type, message_subject, message_body_html, rules_json, cta_label, cta_url } = this.state;
    const errors = {};
    
    if (currentStep === 1) {
      if (!campaign_name || campaign_name.trim() === "") {
        errors.campaign_name = "Automation name is required";
        alert("Please enter an automation name");
        this.setState({ errors });
        return false;
      }
      if (!this.state.campaign_description || this.state.campaign_description.trim() === "") {
        errors.campaign_description = "Automation description is required";
        alert("Please enter an automation description");
        this.setState({ errors });
        return false;
      }
      // Only require audience type for non-system campaigns
      if (campaign_type !== 'system' && !audience_type) {
        alert("Please select an audience type");
        return false;
      }
    }
    
    if (currentStep === 2) {
      if (campaign_type === 'system') {
        // Validate system campaign trigger configuration
        if (!rules_json.trigger_state) {
          alert("Please select a trigger event");
          return false;
        }
        if (!rules_json.trigger_offset_days || rules_json.trigger_offset_days === 0) {
          alert("Please specify the number of days for the trigger");
          return false;
        }
      } else {
        // Check if at least one rule is set for promotional/always_on campaigns
        if (!this.hasValidRules()) {
          alert("Please set at least one targeting rule");
          return false;
        }
      }
    }
    
    if (currentStep === 3) {
      if (!message_subject || message_subject.trim() === "") {
        errors.message_subject = "Subject line is required";
        alert("Please enter a subject line");
        this.setState({ errors });
        return false;
      }
      if (!message_body_html || message_body_html.trim() === "") {
        alert("Please enter a message body");
        return false;
      }
      
      // CTA validation for system campaigns (remarketing campaigns use dropdown so URL is auto-generated)
      if (campaign_type === 'system') {
        // For system campaigns: both or neither
        if ((cta_label && !cta_url) || (!cta_label && cta_url)) {
          alert("Call-to-Action: Both label and URL must be provided together, or leave both empty");
          return false;
        }

        // If CTA URL provided, validate format
        // Allow URLs starting with http://, https://, or {{base_url}} (template variable)
        if (cta_url && !cta_url.match(/^(https?:\/\/|{{base_url}}).+/)) {
          alert("Call-to-Action URL must be a valid URL starting with http://, https://, or {{base_url}}");
          return false;
        }
      }
      // For remarketing campaigns (promotional/always_on), CTA label is required but URL is auto-generated
      // No additional validation needed since dropdown handles the URL
    }
    
    this.setState({ errors });
    return true;
  }

  // Check if valid targeting rules exist (for promotional/always_on campaigns)
  hasValidRules = () => {
    const { rules_json, audience_type } = this.state;

    if (audience_type === 'customers') {
      // Check if any customer targeting rule is set
      return (
        (rules_json.days_since_created_min !== null && rules_json.days_since_created_min !== undefined) ||
        (rules_json.days_since_created_max !== null && rules_json.days_since_created_max !== undefined) ||
        (Array.isArray(rules_json.service_catalog) && rules_json.service_catalog.length > 0)
      );
    } else if (audience_type === 'prospects') {
      // Check if any prospect targeting rule is set
      return (
        (rules_json.days_since_created_min !== null && rules_json.days_since_created_min !== undefined) ||
        (rules_json.days_since_created_max !== null && rules_json.days_since_created_max !== undefined)
      );
    }

    return false;
  }

  // Handlers
  handleCampaignNameChange = (e) => {
    this.setState({ 
      campaign_name: e.target.value,
      errors: { ...this.state.errors, campaign_name: null }
    });
  }

  handleCampaignTypeChange = (e) => {
    const campaign_type = e.target.value;
    this.setState({ campaign_type });

    // Reset rules_json and CTA when switching campaign types
    if (campaign_type === 'system') {
      this.setState({
        rules_json: {
          trigger_state: null,
          trigger_offset_days: null,
        },
        audience_type: 'request',
        cta_type: 'estimate_page',
        cta_label: 'Get a Quote',
        cta_url: ''
      });
    } else {
      // For remarketing campaigns, set default CTA based on type
      const defaultCtaType = campaign_type === 'promotional' ? 'email_specialist' : 'estimate_page';
      const defaultCtaLabel = campaign_type === 'promotional' ? 'Email Us' : 'Get a Quote';

      this.setState({
        rules_json: {
          days_since_created_min: null,
          days_since_created_max: null,
          service_catalog: [],
        },
        audience_type: 'customers', // Default
        cta_type: defaultCtaType,
        cta_label: defaultCtaLabel,
        cta_url: '' // Will be generated on save
      });
    }
  }

  handleAudienceTypeChange = (e) => {
    const audience_type = e.target.value;

    // Update audience_type and reset rules_json (no customer_type needed)
    this.setState({
      audience_type,
      rules_json: {
        days_since_created_min: null,
        days_since_created_max: null,
        service_catalog: [],
      }
    });
  }

  // Generate the estimate URL for campaign emails
  generateEstimateUrl = () => {
    // Use template variables that will be replaced when emails are sent
    // {{base_url}} is replaced with the correct frontend URL by the email sender
    // {{campaign_id}} and {{customer_id}} are also replaced at send time
    return `{{base_url}}/estimate?installer={{installer_id}}&cid={{campaign_id}}&customer_id={{customer_id}}`;
  }

  // Insert the generated estimate URL into the CTA URL field
  handleInsertEstimateUrl = () => {
    const estimateUrl = this.generateEstimateUrl();
    this.setState({ cta_url: estimateUrl });
  }

  handlePromotionalStartDateChange = (e) => {
    this.setState({ promotional_start_date: e.target.value });
  }

  handlePromotionalEndDateChange = (e) => {
    this.setState({ promotional_end_date: e.target.value });
  }

  handleRuleChange = (field, value) => {
    const newRules = { ...this.state.rules_json };
    
    // Convert to number if not empty, otherwise null
    if (value === '' || value === null) {
      newRules[field] = null;
    } else {
      const numValue = parseInt(value);
      newRules[field] = isNaN(numValue) ? null : numValue;
    }
    
    this.setState({ rules_json: newRules });
  }

  handleServiceCatalogChange = (selected) => {
    const newRules = { ...this.state.rules_json };
    newRules.service_catalog = selected;
    this.setState({ rules_json: newRules });
  }

  // System campaign trigger handlers
  handleTriggerStateChange = (e) => {
    const trigger_state = e.target.value;
    const { rules_json } = this.state;
    
    // Force 'after' (positive) direction for 'created' and 'collected' triggers
    let trigger_offset_days = rules_json.trigger_offset_days || 2;
    if (trigger_state === 'created' || trigger_state === 'collected') {
      trigger_offset_days = Math.abs(trigger_offset_days);
    }
    
    this.setState({
      rules_json: {
        ...rules_json,
        trigger_state,
        trigger_offset_days
      }
    });
  }

  handleTriggerOffsetChange = (e) => {
    const { rules_json } = this.state;
    const absValue = parseInt(e.target.value) || 2;
    
    // Preserve the sign (positive/negative) when changing the number
    const currentSign = rules_json.trigger_offset_days < 0 ? -1 : 1;
    
    this.setState({
      rules_json: {
        ...rules_json,
        trigger_offset_days: absValue * currentSign
      }
    });
  }

  handleTimingDirectionChange = (e) => {
    const { rules_json } = this.state;
    const direction = parseInt(e.target.value); // -1 for before, 1 for after
    const absValue = Math.abs(rules_json.trigger_offset_days || 2);
    
    this.setState({
      rules_json: {
        ...rules_json,
        trigger_offset_days: absValue * direction
      }
    });
  }

  getTriggerPreviewText = () => {
    const { rules_json } = this.state;
    
    if (!rules_json.trigger_state || !rules_json.trigger_offset_days) {
      return "Configure trigger settings above to see preview";
    }
    
    const stateLabels = {
      'created': 'estimate is created',
      'scheduled': 'job is scheduled to start',
      'collected': 'vehicle is collected'
    };
    
    const absDay = Math.abs(rules_json.trigger_offset_days);
    const dayText = absDay === 1 ? 'day' : 'days';
    const timing = rules_json.trigger_offset_days < 0 ? 'before' : 'after';
    
    return `Email will be sent ${absDay} ${dayText} ${timing} ${stateLabels[rules_json.trigger_state]}`;
  }

  handleSubjectChange = (e) => {
    this.setState({ message_subject: e.target.value });
  }

  handleBodyChange = (content) => {
    console.log("ReactQuill content changed:", content);
    console.log("Content type:", typeof content);
    console.log("Content length:", content?.length);
    this.setState({ message_body_html: content });
  }

  handleCtaLabelChange = (e) => {
    this.setState({ cta_label: e.target.value });
  }

  handleCtaUrlChange = (e) => {
    this.setState({ cta_url: e.target.value });
  }

  // Determine CTA type from existing URL (for editing campaigns)
  determineCTAType = (ctaUrl, campaignType) => {
    if (!ctaUrl) {
      // Default for promotional campaigns is email_specialist
      return campaignType === 'promotional' ? 'email_specialist' : 'estimate_page';
    }
    if (ctaUrl.startsWith('mailto:')) {
      return 'email_specialist';
    }
    if (ctaUrl.includes('{{base_url}}/estimate') || ctaUrl.includes('/estimate?')) {
      return 'estimate_page';
    }
    if (ctaUrl === '{{specialist_website}}' || ctaUrl.startsWith('http')) {
      return 'specialist_website';
    }
    return 'estimate_page';
  }

  // Generate URLs for each CTA type
  generateCTAUrl = (ctaType) => {
    switch (ctaType) {
      case 'email_specialist':
        // mailto link with campaign name as subject
        return `mailto:{{specialist_email}}?subject=${encodeURIComponent(this.state.campaign_name || 'Inquiry')}`;
      case 'estimate_page':
        return this.generateEstimateUrl();
      case 'specialist_website':
        return '{{specialist_website}}';
      default:
        return '';
    }
  }

  // Generate default CTA label for each type
  generateCTALabel = (ctaType) => {
    switch (ctaType) {
      case 'email_specialist':
        return 'Email Us';
      case 'estimate_page':
        return 'Get a Quote';
      case 'specialist_website':
        return 'Visit Our Website';
      default:
        return 'Get a Quote';
    }
  }

  handleCtaTypeChange = (e) => {
    const ctaType = e.target.value;
    const ctaUrl = this.generateCTAUrl(ctaType);
    const ctaLabel = this.generateCTALabel(ctaType);

    this.setState({
      cta_type: ctaType,
      cta_url: ctaUrl,
      cta_label: ctaLabel
    });
  }

  handleDeliveryTimeChange = (e) => {
    this.setState({ delivery_time: e.target.value });
  }

  // Variable insertion for subject line
  insertSubjectVariable = (variable) => {
    const { message_subject } = this.state;
    const cursorPos = this.subjectInputRef ? this.subjectInputRef.selectionStart : message_subject.length;
    const before = message_subject.substring(0, cursorPos);
    const after = message_subject.substring(cursorPos);
    const newSubject = `${before}{{${variable}}}${after}`;
    
    this.setState({ message_subject: newSubject });
  }

  // Variable insertion for message body (Quill editor)
  insertVariable = (variable) => {
    if (this.quillRef) {
      const quill = this.quillRef.getEditor();
      const range = quill.getSelection(true);
      quill.insertText(range.index, `{{${variable}}}`);
      quill.setSelection(range.index + variable.length + 4);
    }
  }

  // Quill editor configuration
  quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  };

  quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'align',
    'link'
  ];

  // Convert HTML to plain text
  htmlToPlainText = (html) => {
    if (!html) return '';

    // Create a temporary div element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Get the text content (strips all HTML tags)
    const text = tempDiv.textContent || tempDiv.innerText || '';

    // Clean up extra whitespace and return
    return text.trim();
  }

  // Calculate audience size for preview (only for promotional/always_on campaigns)
  calculateAudienceSize = async () => {
    const { campaign_type, audience_type, rules_json } = this.state;

    // Skip for system campaigns
    if (campaign_type === 'system') {
      return;
    }

    this.setState({ audienceLoading: true });

    try {
      // Prepare rules for backend
      let backendRules = { ...rules_json };

      // FORWARD MAPPING: Convert UI field names to backend field names
      // UI uses: audience_type ("customers"/"prospects") + days_since_created_min/max
      // Backend expects: trigger_state ("completed"/"created") + days_since_trigger_min/max

      // Set trigger_state based on audience_type
      if (audience_type === 'customers') {
        backendRules.trigger_state = 'completed';
      } else if (audience_type === 'prospects') {
        backendRules.trigger_state = 'created';
      }

      // Map UI days_since_created_* to backend days_since_trigger_*
      if (backendRules.days_since_created_min !== undefined && backendRules.days_since_created_min !== null) {
        backendRules.days_since_trigger_min = backendRules.days_since_created_min;
      }
      if (backendRules.days_since_created_max !== undefined && backendRules.days_since_created_max !== null) {
        backendRules.days_since_trigger_max = backendRules.days_since_created_max;
      }

      // Clean up UI-only fields
      delete backendRules.days_since_created_min;
      delete backendRules.days_since_created_max;

      console.log("Calculating audience with rules:", backendRules);

      const response = await API.post('/campaigns/preview-audience', {
        rules_json: backendRules
      });

      console.log("Audience preview response:", response);

      if (response.data && typeof response.data.count !== 'undefined') {
        this.setState({
          audienceCount: response.data.count,
          audienceLoading: false
        });
      } else {
        this.setState({
          audienceCount: 0,
          audienceLoading: false
        });
      }
    } catch (error) {
      console.error("Error calculating audience size:", error);
      this.setState({
        audienceCount: 0,
        audienceLoading: false
      });
    }
  }

  // Save campaign
  saveCampaign = async () => {
    const {
      campaign_name,
      campaign_type,
      audience_type,
      promotional_start_date,
      promotional_end_date,
      rules_json,
      message_subject,
      message_body_html,
      cta_label,
      cta_url,
      cta_type,
      delivery_time
    } = this.state;

    // For remarketing campaigns, ensure CTA URL is generated from cta_type
    let finalCtaUrl = cta_url;
    if (campaign_type !== 'system') {
      finalCtaUrl = this.generateCTAUrl(cta_type);
    }

    // Prepare rules for backend
    let backendRules = { ...rules_json };

    if (campaign_type === 'system') {
      // For system campaigns, only send trigger_state and trigger_offset_days
      backendRules = {
        trigger_state: rules_json.trigger_state,
        trigger_offset_days: rules_json.trigger_offset_days
      };
    } else {
      // FORWARD MAPPING for Always-On/Promotional campaigns
      // UI uses: audience_type ("customers"/"prospects") + days_since_created_min/max
      // Backend expects: trigger_state ("completed"/"created") + days_since_trigger_min/max

      // Set trigger_state based on audience_type
      if (audience_type === 'customers') {
        backendRules.trigger_state = 'completed';
      } else if (audience_type === 'prospects') {
        backendRules.trigger_state = 'created';
      }

      // Map UI days_since_created_* to backend days_since_trigger_*
      if (backendRules.days_since_created_min !== undefined && backendRules.days_since_created_min !== null) {
        backendRules.days_since_trigger_min = backendRules.days_since_created_min;
      }
      if (backendRules.days_since_created_max !== undefined && backendRules.days_since_created_max !== null) {
        backendRules.days_since_trigger_max = backendRules.days_since_created_max;
      }

      // Clean up UI-only fields
      delete backendRules.days_since_created_min;
      delete backendRules.days_since_created_max;
    }

    // Convert rules_json to string for storage
    const rules_json_string = JSON.stringify(backendRules);

    // Convert HTML to plain text for message_body
    const plainTextBody = this.htmlToPlainText(message_body_html);

    console.log("=== CAMPAIGN SAVE DEBUG ===");
    console.log("message_body_html (raw):", message_body_html);
    console.log("message_body_html length:", message_body_html?.length);
    console.log("plainTextBody:", plainTextBody);
    console.log("plainTextBody length:", plainTextBody?.length);

    // Determine campaign status
    // - New campaigns always start as "draft"
    // - When editing, preserve the existing status (running campaigns stay running)
    const isEditMode = this.props.mode === "edit" && this.props.campaign && this.props.campaign.id;
    const campaign_status = isEditMode ? this.props.campaign.campaign_status : "draft";

    const campaignData = {
      campaign_name,
      campaign_type,
      campaign_status, // Preserve status for existing campaigns, "draft" for new ones
      promotional_start_date: campaign_type === 'promotional' ? promotional_start_date : null,
      promotional_end_date: campaign_type === 'promotional' ? promotional_end_date : null,
      rules_json: rules_json_string,
      message_subject,
      message_body: plainTextBody, // Store plain text for display in CampaignDetails
      message_body_html, // Store HTML for email sending
      cta_label: cta_label || null,
      cta_url: finalCtaUrl || null,
      delivery_time,
      // Description is available for all campaign types
      campaign_description: this.state.campaign_description || null,
      // Promo code and discount only for promotional/always_on campaigns
      promo_code: campaign_type !== 'system' ? (this.state.promo_code || null) : null,
      discount_description: campaign_type !== 'system' ? (this.state.discount_description || null) : null
    };

    console.log("Saving campaign:", campaignData);
    console.log("message_body_html in campaignData:", campaignData.message_body_html);

    this.setState({ loading: true });

    try {
      let response;
      if (this.props.mode === "edit" && this.props.campaign && this.props.campaign.id) {
        // Update existing campaign
        response = await API.put(`/campaigns/${this.props.campaign.id}`, campaignData);
        ReactGA.event({
          category: 'Campaign',
          action: 'Updated Campaign',
          label: campaign_type
        });
      } else {
        // Create new campaign
        response = await API.post('/campaigns', campaignData);
        ReactGA.event({
          category: 'Campaign',
          action: 'Created Campaign',
          label: campaign_type
        });
      }

      console.log("Campaign saved successfully:", response);
      console.log("Response data:", response.data);
      console.log("Returned message_body_html:", response.data?.message_body_html);

      alert('Automation saved.');

      // Close wizard and return to campaign list
      if (this.props.onCancel) {
        this.props.onCancel();
      }
    } catch (error) {
      console.error("Error saving campaign:", error);
      alert(`Error saving automation: ${error.message || 'Unknown error'}`);
    } finally {
      this.setState({ loading: false });
    }
  }

  // Send preview email
  sendPreviewEmail = async () => {
    const {
      message_subject,
      message_body_html,
      cta_label,
      cta_url,
      campaign_type
    } = this.state;

    // Validation
    if (!message_subject || message_subject.trim() === "") {
      alert("Please enter a subject line before sending preview");
      return;
    }

    if (!message_body_html || message_body_html.trim() === "") {
      alert("Please enter a message body before sending preview");
      return;
    }

    // Get installer email from company props
    const installerEmail = this.props.company?.installer_email;
    if (!installerEmail) {
      alert("Unable to send preview email: Installer email not found");
      return;
    }

    this.setState({ loading: true });

    try {
      const response = await API.post('/campaigns/preview-email', {
        preview_email: installerEmail,
        message_subject,
        message_body_html,
        cta_label: cta_label || null,
        cta_url: cta_url || null,
        campaign_type
      });

      if (response.data && response.data.success) {
        alert(`Preview email sent to ${installerEmail}! Check your inbox.`);
        ReactGA.event({
          category: 'Campaign',
          action: 'Sent Preview Email',
          label: campaign_type
        });
      }
    } catch (error) {
      console.error("Error sending preview email:", error);
      alert(`Error sending preview email: ${error.message || 'Unknown error'}`);
    } finally {
      this.setState({ loading: false });
    }
  }

  render() {
    const {
      currentStep,
      campaign_name,
      campaign_type,
      audience_type,
      rules_json,
      message_subject,
      message_body_html,
      cta_label,
      cta_url,
      cta_type,
      delivery_time,
      audienceCount,
      audienceLoading
    } = this.state;

    return (
      <div className="campaign-wizard">
                {/* Custom styling for ReactQuill editor - increase font size */}
        <style>{`
          .campaign-wizard .ql-editor {
            font-size: 16px !important;
            line-height: 1.6;
          }
          .campaign-wizard .ql-editor p {
            font-size: 16px !important;
          }
        `}</style>
        {/* Header - Light gray background with black text and X button */}
        <div style={{
          backgroundColor: 'var(--color-bg-panel)',
          padding: '15px 20px',
          borderRadius: '8px 8px 0 0',
          position: 'relative',
          marginBottom: '0'
        }}>
          <h4 className="mb-0" style={{ color: 'var(--color-text-primary)', fontWeight: '400', textAlign: 'center' }}>
            {this.props.mode === "edit" ? "Edit Automation" : "Create New Automation"}
          </h4>
          <button
            type="button"
            onClick={() => {
              console.log("Cancel clicked!");
              if (this.props.onCancel) {
                this.props.onCancel();
              } else if (this.props.onClose) {
                this.props.onClose();
              } else {
                console.warn("No onCancel or onClose prop provided");
              }
            }}
            style={{
              position: 'absolute',
              right: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
              padding: '0',
              lineHeight: '1',
              width: '24px',
              height: '24px'
            }}
          >
            ×
          </button>
        </div>

        {/* Progress Steps */}
        <div style={{ backgroundColor: 'var(--color-bg-panel)', padding: '30px 20px', marginBottom: '20px' }}>
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <div className={`flex-fill text-center ${currentStep >= 1 ? 'text-primary' : 'text-muted'}`}>
                  <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${currentStep >= 1 ? 'bg-primary text-white' : 'bg-light'}`} 
                       style={{ width: '50px', height: '50px', fontSize: '20px' }}>
                    {currentStep > 1 ? <i className="fa-sharp fa-regular fa-check"></i> : '1'}
                  </div>
                  <div className="mt-2"><strong>Setup</strong></div>
                </div>
                <div className="flex-fill" style={{ height: '2px', backgroundColor: currentStep >= 2 ? 'var(--color-info)' : 'var(--color-bg-inset)', margin: '0 10px' }}></div>
                <div className={`flex-fill text-center ${currentStep >= 2 ? 'text-primary' : 'text-muted'}`}>
                  <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${currentStep >= 2 ? 'bg-primary text-white' : 'bg-light'}`} 
                       style={{ width: '50px', height: '50px', fontSize: '20px' }}>
                    {currentStep > 2 ? <i className="fa-sharp fa-regular fa-check"></i> : '2'}
                  </div>
                  <div className="mt-2"><strong>When It Sends</strong></div>
                </div>
                <div className="flex-fill" style={{ height: '2px', backgroundColor: currentStep >= 3 ? 'var(--color-info)' : 'var(--color-bg-inset)', margin: '0 10px' }}></div>
                <div className={`flex-fill text-center ${currentStep >= 3 ? 'text-primary' : 'text-muted'}`}>
                  <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${currentStep >= 3 ? 'bg-primary text-white' : 'bg-light'}`} 
                       style={{ width: '50px', height: '50px', fontSize: '20px' }}>
                    3
                  </div>
                  <div className="mt-2"><strong>Message Content</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wizard Content */}
        <div className="shadow-sm p-4 corners bg-white">
          {/* STEP 1: Campaign Info */}
          {currentStep === 1 && (
            <div className="campaign-info-step">
              <h4 className="text-center mb-4">Setup</h4>
              
              <div className="form-group">
                <label><strong>Automation Name *</strong></label>
                <input
                  type="text"
                  className="form-control form-control-lg"
                  value={campaign_name}
                  onChange={this.handleCampaignNameChange}
                  ref={(ref) => this.campaignNameRef = ref}
                />
              </div>

              <div className="form-group">
                <label><strong>Automation Description *</strong></label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={this.state.campaign_description}
                  onChange={(e) => this.setState({ campaign_description: e.target.value })}
                  placeholder="Describe the purpose of this automation"
                />
              </div>

              <div className="form-group">
                <label><strong>Purpose *</strong></label>
                <select
                  className="form-control form-control-lg"
                  value={campaign_type}
                  onChange={this.handleCampaignTypeChange}
                  disabled={this.props.agentType === 'iris'}>
                  {(!this.props.agentType || this.props.agentType === 'luke') && (
                    <>
                      <option value="always_on">Always-On (Runs continuously)</option>
                      <option value="promotional">Promotional (Time-limited)</option>
                    </>
                  )}
                  {(!this.props.agentType || this.props.agentType === 'iris') && (
                    <option value="system">CX Communication (Automated job lifecycle emails)</option>
                  )}
                </select>
                <small className="text-muted d-block mt-2">
                  {campaign_type === 'always_on' && "Continuous automations run indefinitely"}
                  {campaign_type === 'promotional' && "Promotional automations run between specific dates"}
                  {campaign_type === 'system' && "Automated emails sent based on job status (appointment reminders, follow-ups, etc.)"}
                </small>
              </div>

              {campaign_type === "promotional" && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label><strong>Start Date</strong></label>
                      <input
                        type="date"
                        className="form-control form-control-lg"
                        value={this.state.promotional_start_date}
                        onChange={(e) => this.setState({ promotional_start_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label><strong>End Date</strong></label>
                      <input
                        type="date"
                        className="form-control form-control-lg"
                        value={this.state.promotional_end_date}
                        onChange={(e) => this.setState({ promotional_end_date: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Promo Code and Discount - Only for promotional/always_on */}
              {campaign_type !== 'system' && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label><strong>Promo Code</strong></label>
                      <input
                        type="text"
                        className="form-control"
                        value={this.state.promo_code}
                        onChange={(e) => this.setState({ promo_code: e.target.value.toUpperCase() })}
                        placeholder="e.g., SUMMER2024"
                        maxLength={20}
                      />
                      <small className="text-muted">
                        Reference code for tracking (shown to front desk)
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label><strong>Discount Description</strong></label>
                      <input
                        type="text"
                        className="form-control"
                        value={this.state.discount_description}
                        onChange={(e) => this.setState({ discount_description: e.target.value })}
                        placeholder="e.g., 15% off window tint"
                        maxLength={100}
                      />
                      <small className="text-muted">
                        Human-readable discount (front desk applies manually)
                      </small>
                    </div>
                  </div>
                </div>
              )}

              {/* Audience Type - Only for promotional/always_on */}
              {campaign_type !== 'system' && (
                <div className="form-group">
                  <label><strong>Target Audience *</strong></label>
                  <div className="row">
                    <div className="col-md-6">
                      <div className={`card ${audience_type === 'customers' ? 'border-primary' : ''}`} 
                           style={{ cursor: 'pointer' }}
                           onClick={() => this.handleAudienceTypeChange({ target: { value: 'customers' } })}>
                        <div className="card-body text-center">
                          <input 
                            type="radio" 
                            name="audience_type" 
                            value="customers"
                            checked={audience_type === 'customers'}
                            onChange={this.handleAudienceTypeChange}
                            className="mr-2"
                          />
                          <i className="fa-sharp fa-regular fa-users fa-3x text-success mb-3 d-block"></i>
                          <h5>Existing Customers</h5>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className={`card ${audience_type === 'prospects' ? 'border-primary' : ''}`} 
                           style={{ cursor: 'pointer' }}
                           onClick={() => this.handleAudienceTypeChange({ target: { value: 'prospects' } })}>
                        <div className="card-body text-center">
                          <input 
                            type="radio" 
                            name="audience_type" 
                            value="prospects"
                            checked={audience_type === 'prospects'}
                            onChange={this.handleAudienceTypeChange}
                            className="mr-2"
                          />
                          <i className="fa-sharp fa-regular fa-user-plus fa-3x text-info mb-3 d-block"></i>
                          <h5>Prospects</h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Audience Rules OR System Trigger */}
          {currentStep === 2 && (
            <div className="audience-rules-step">
              {campaign_type === 'system' ? (
                // System Campaign Trigger Configuration
                <div>
                  <h4 className="text-center mb-4">When It Sends</h4>
                  <div className="alert alert-info">
                    <i className="fa-sharp fa-regular fa-info-circle"></i> This email will be sent automatically for each retail job that matches the timing below.
                  </div>

                  {/* Single-line Trigger Configuration */}
                  <div className="card mb-3">
                    <div className="card-header bg-light">
                      <strong>Email Trigger Configuration *</strong>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        {/* Number of days */}
                        <div className="col-md-3 col-sm-12 mb-3 mb-md-0">
                          <label><strong># of Days:</strong></label>
                          <input 
                            type="number"
                            min="1"
                            max="30"
                            className="form-control"
                            value={Math.abs(rules_json.trigger_offset_days || 2)}
                            onChange={this.handleTriggerOffsetChange}
                            placeholder="2"
                          />
                        </div>
                        
                        {/* Timing (before/after) */}
                        <div className="col-md-3 col-sm-12 mb-3 mb-md-0">
                          <label><strong>Timing:</strong></label>
                          <select 
                            className="form-control"
                            value={rules_json.trigger_offset_days < 0 ? -1 : 1}
                            onChange={this.handleTimingDirectionChange}
                            disabled={rules_json.trigger_state === 'created' || rules_json.trigger_state === 'collected'}
                          >
                            <option value="-1">before</option>
                            <option value="1">after</option>
                          </select>
                        </div>
                        
                        {/* Trigger Event */}
                        <div className="col-md-6 col-sm-12">
                          <label><strong>Trigger Event:</strong></label>
                          <select 
                            className="form-control"
                            value={rules_json.trigger_state || ''}
                            onChange={this.handleTriggerStateChange}
                          >
                            <option value="">Select event...</option>
                            <option value="created">estimate is created</option>
                            <option value="scheduled">job is scheduled to start</option>
                            <option value="collected">vehicle is collected</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Helper text */}
                      <div className="mt-3">
                        <small className="text-muted">
                          {rules_json.trigger_state === 'created' && "💡 Use for follow-ups on unbooked estimates (only 'after' timing available)"}
                          {rules_json.trigger_state === 'scheduled' && "💡 Use for appointment reminders"}
                          {rules_json.trigger_state === 'collected' && "💡 Use for reviews, referrals, or follow-ups (only 'after' timing available)"}
                          {!rules_json.trigger_state && "💡 Select a trigger event to continue"}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="alert alert-success">
                    <strong>Preview:</strong><br/>
                    {this.getTriggerPreviewText()}
                  </div>
                </div>
              ) : (
                // Promotional/Always-On Audience Rules
                <>
                  <h4 className="text-center mb-4">
                    When It Sends: {audience_type === "customers" ? "Customers" : "Prospects"}
                  </h4>

                  {/* Prospects Rules */}
                  {audience_type === "prospects" && (
                    <React.Fragment>
                      <div className="card mb-3">
                        <div className="card-header bg-light">
                          <strong>Last estimate created in this range</strong>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>From (days ago)</label>
                                <input 
                                  type="number"
                                  className="form-control"
                                  value={rules_json.days_since_created_min || ""}
                                  onChange={(e) => this.handleRuleChange('days_since_created_min', e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>To (days ago)</label>
                                <input 
                                  type="number"
                                  className="form-control"
                                  value={rules_json.days_since_created_max || ""}
                                  onChange={(e) => this.handleRuleChange('days_since_created_max', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  )}

                  {/* Customers Rules */}
                  {audience_type === "customers" && (
                    <React.Fragment>
                      <div className="card mb-3">
                        <div className="card-header bg-light">
                          <strong>Last completed job in this range</strong>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>From (days ago)</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={rules_json.days_since_created_min || ""}
                                  onChange={(e) => this.handleRuleChange('days_since_created_min', e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>To (days ago)</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={rules_json.days_since_created_max || ""}
                                  onChange={(e) => this.handleRuleChange('days_since_created_max', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Service Category Filter */}
                      <div className="card mb-3">
                        <div className="card-header bg-light">
                          <strong>Filter by service type (optional)</strong>
                        </div>
                        <div className="card-body">
                          <div className="form-group">
                            <select 
                              className="form-control"
                              multiple
                              size="4"
                              value={rules_json.service_catalog || []}
                              onChange={(e) => {
                                const selected = Array.from(e.target.selectedOptions, option => option.value);
                                this.handleServiceCatalogChange(selected);
                              }}
                            >
                              <option value="PPF">PPF (Paint Protection Film)</option>
                              <option value="TINT">Window Tint</option>
                              <option value="DETAILING">Detailing</option>
                              <option value="CERAMIC">Ceramic Coating</option>
                            </select>
                            <small className="text-muted d-block mt-2">
                              Hold Ctrl (Cmd on Mac) to select multiple services
                            </small>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  )}
                </>
              )}
            </div>
          )}

          {/* STEP 3: Message */}
          {currentStep === 3 && (
            <div className="message-step">
              <h4 className="text-center mb-4">Message Content</h4>

              {/* Audience preview for promotional/always_on campaigns */}
              {campaign_type !== 'system' && (
                <div className="alert alert-info mb-4">
                  <strong>Estimated Audience Size: </strong>
                  {audienceLoading ? (
                    <span>Calculating...</span>
                  ) : (
                    <span>{audienceCount.toLocaleString()} people</span>
                  )}
                </div>
              )}

              {/* Subject Line */}
              <div className="form-group">
                <label><strong>Subject Line *</strong></label>
                <input 
                  type="text"
                  className="form-control form-control-lg"
                  value={message_subject}
                  onChange={this.handleSubjectChange}
                  ref={(ref) => this.subjectInputRef = ref}
                  placeholder="e.g., Special Offer Just For You!"
                />
                <div className="mt-2">
                  <small className="text-muted">Insert variables in Subject: </small>
                  <button className="btn btn-sm btn-outline-secondary ml-2" onClick={() => this.insertSubjectVariable('customer_name')}>
                    Customer Name
                  </button>
                  <button className="btn btn-sm btn-outline-secondary ml-2" onClick={() => this.insertSubjectVariable('specialist_name')}>
                    Your Business Name
                  </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary m-1" 
                                onClick={() => this.insertSubjectVariable('vehicle')}>
                          Vehicle
                        </button>

                </div>
              </div>

              {/* Message Body */}
              <div className="form-group">
                <label><strong>Message Body *</strong></label>
                <ReactQuill
                  ref={(el) => { this.quillRef = el }}
                  theme="snow"
                  value={message_body_html}
                  onChange={this.handleBodyChange}
                  modules={this.quillModules}
                  formats={this.quillFormats}
                  placeholder="Write your email message here..."
                  style={{ height: '250px', marginBottom: '50px' }}
                />
              </div>
                            {/* Message Body Variables */}
              <div className="card bg-light mb-3">
                <div className="card-body">
                  <strong>Insert variables in Body:</strong>
                  <div className="mt-2">
                    {/* Customer Data */}
                    <div className="mb-2">
                      <small className="text-muted d-block mb-1"><strong>Customer Data:</strong></small>
                      <div className="btn-group flex-wrap" role="group">
                        <button type="button" className="btn btn-sm btn-outline-primary m-1" 
                                onClick={() => this.insertVariable('first_name')}>
                          {'{{'}first_name{'}}'}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-primary m-1" 
                                onClick={() => this.insertVariable('last_name')}>
                          {'{{'}last_name{'}}'}
                        </button>

                      </div>
                    </div>

                    {/* Vehicle Data */}
                    <div className="mb-2">
                      <small className="text-muted d-block mb-1"><strong>Vehicle Data:</strong></small>
                      <div className="btn-group flex-wrap" role="group">
                        <button type="button" className="btn btn-sm btn-outline-success m-1" 
                                onClick={() => this.insertVariable('vehicle')}>
                          {'{{'}vehicle{'}}'}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-success m-1" 
                                onClick={() => this.insertVariable('vehicle_make')}>
                          {'{{'}vehicle_make{'}}'}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-success m-1" 
                                onClick={() => this.insertVariable('vehicle_model')}>
                          {'{{'}vehicle_model{'}}'}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-success m-1" 
                                onClick={() => this.insertVariable('vehicle_year')}>
                          {'{{'}vehicle_year{'}}'}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-success m-1" 
                                onClick={() => this.insertVariable('vehicle_trim')}>
                          {'{{'}vehicle_trim{'}}'}
                        </button>
                      </div>
                    </div>

                    {/* Service Data */}
                    <div className="mb-2">
                      <small className="text-muted d-block mb-1"><strong>Service Data:</strong></small>
                      <div className="btn-group flex-wrap" role="group">
                        <button type="button" className="btn btn-sm btn-outline-info m-1" 
                                onClick={() => this.insertVariable('last_service')}>
                          {'{{'}last_service{'}}'}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-info m-1" 
                                onClick={() => this.insertVariable('last_service_date')}>
                          {'{{'}last_service_date{'}}'}
                        </button>
                      </div>
                    </div>

                    {/* Specialist Data */}
                    <div className="mb-2">
                      <small className="text-muted d-block mb-1"><strong>Your Business Data:</strong></small>
                      <div className="btn-group flex-wrap" role="group">
                        <button type="button" className="btn btn-sm btn-outline-warning m-1"
                                onClick={() => this.insertVariable('specialist_name')}>
                          {'{{'}specialist_name{'}}'}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-warning m-1"
                                onClick={() => this.insertVariable('specialist_email')}>
                          {'{{'}specialist_email{'}}'}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-warning m-1"
                                onClick={() => this.insertVariable('specialist_phone')}>
                          {'{{'}specialist_phone{'}}'}
                        </button>
                      </div>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-2">
                    <i className="fa-sharp fa-regular fa-info-circle"></i> Click a button to insert the variable at your cursor position. These will be replaced with real data when emails are sent.
                  </small>
                </div>
              </div>


              {/* Call to Action */}
              <div className="card mb-3">
                <div className="card-header bg-light">
                  <strong>Call-to-Action Button</strong>
                </div>
                <div className="card-body">
                  {/* For remarketing campaigns (promotional/always_on), show dropdown */}
                  {campaign_type !== 'system' ? (
                    <>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Button Action</label>
                            <select
                              className="form-control"
                              value={cta_type}
                              onChange={this.handleCtaTypeChange}
                            >
                              <option value="email_specialist">Email Specialist</option>
                              <option value="estimate_page">Estimate Page</option>
                              <option value="specialist_website">Specialist Website</option>
                            </select>
                            <small className="text-muted">
                              {cta_type === 'email_specialist' && 'Opens customer\'s email app to contact you'}
                              {cta_type === 'estimate_page' && 'Links to your online estimate page'}
                              {cta_type === 'specialist_website' && 'Links to your business website'}
                            </small>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Button Label</label>
                            <input
                              type="text"
                              className="form-control"
                              value={cta_label}
                              onChange={this.handleCtaLabelChange}
                              maxLength={25}
                            />
                            <small className="text-muted">
                              {cta_label.length}/25 characters
                            </small>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    /* For system campaigns, keep the original manual URL input */
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Button Label</label>
                          <input
                            type="text"
                            className="form-control"
                            value={cta_label}
                            onChange={this.handleCtaLabelChange}
                            maxLength={25}
                          />
                          <small className="text-muted">
                            {cta_label.length}/25 characters
                          </small>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Button URL</label>
                          <input
                            type="text"
                            className="form-control"
                            value={cta_url}
                            onChange={this.handleCtaUrlChange}
                            placeholder="https://"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Time - For all campaign types */}
              <div className="form-group">
                <label><strong>Delivery Time</strong></label>
                <input
                  type="time"
                  className="form-control form-control-lg"
                  value={delivery_time}
                  onChange={this.handleDeliveryTimeChange}
                />
                <small className="text-muted">Time of day to send emails (in your local timezone)</small>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="d-flex justify-content-between mt-4">
            {currentStep > 1 && (
              <button 
                className="btn btn-secondary"
                onClick={this._prev}
                disabled={this.state.loading}
              >
                <i className="fa-sharp fa-regular fa-arrow-left mr-2"></i>
                PREVIOUS
              </button>
            )}

            <div className="ml-auto">
              {currentStep < 3 ? (
                <button
                  className="btn btn-primary"
                  onClick={this._next}
                  disabled={this.state.loading}
                >
                  NEXT
                  <i className="fa-sharp fa-regular fa-arrow-right ml-2"></i>
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-info mr-2"
                    onClick={this.sendPreviewEmail}
                    disabled={this.state.loading || !message_subject || !message_body_html}
                    title="Send a test email to your inbox"
                  >
                    <i className="fa-sharp fa-regular fa-envelope mr-2"></i>
                    SEND PREVIEW EMAIL
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={this.saveCampaign}
                    disabled={this.state.loading}
                  >
                    {this.state.loading ? (
                      <>
                        <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
                        SAVING...
                      </>
                    ) : (
                      <>
                        <i className="fa-sharp fa-regular fa-check mr-2"></i>
                        SAVE AUTOMATION
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(CampaignWizard);